import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Users, Mail, Swords, CalendarDays, Images, Handshake, Film, LogOut, Trash2, Download, Plus, Pencil, ArrowUp, ArrowDown, Star } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SpadeMark } from "@/components/brand";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import HeroMediaTab from "@/pages/admin/HeroMediaTab";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "registrations", label: "Registrations", icon: Users },
  { key: "enquiries", label: "Enquiries", icon: Mail },
  { key: "events", label: "Events", icon: Swords },
  { key: "schedule", label: "Schedule", icon: CalendarDays },
  { key: "gallery", label: "Gallery", icon: Images },
  { key: "sponsors", label: "Sponsors", icon: Handshake },
  { key: "hero", label: "Hero Media", icon: Film },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

function StatCard({ label, value, testId }) {
  return (
    <div className="border border-white/8 bg-[#111116] p-6" data-testid={testId}>
      <p className="font-display text-3xl font-bold text-[#E5C97A]">{value}</p>
      <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#A19E95]">{label}</p>
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => toast.error("Failed to load stats"));
  }, []);
  if (!stats) return <p className="text-sm text-[#6E6B63]">Counting the chips...</p>;
  return (
    <div data-testid="admin-overview">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard label="Total Registrations" value={stats.total_registrations} testId="stat-total-registrations" />
        <StatCard label="Individual Entries" value={stats.individual} testId="stat-individual" />
        <StatCard label="Group Contingents" value={stats.group} testId="stat-group" />
        <StatCard label="Total Delegates" value={stats.delegates} testId="stat-delegates" />
        <StatCard label="Unread Enquiries" value={stats.unread_enquiries} testId="stat-unread" />
        <StatCard label="Open Events" value={stats.events_active} testId="stat-events" />
      </div>
      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <div className="border border-white/8 bg-[#111116] p-6">
          <p className="eyebrow mb-5">Event Tally</p>
          {Object.keys(stats.per_event).length === 0 && <p className="text-sm text-[#6E6B63]">No entries yet.</p>}
          <div className="flex flex-col gap-3">
            {Object.entries(stats.per_event).map(([name, count]) => (
              <div key={name} className="flex items-center justify-between text-sm">
                <span className="text-[#F6F4EE]/85">{name}</span>
                <span className="font-mono text-xs text-[#E5C97A]">{count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/8 bg-[#111116] p-6">
          <p className="eyebrow mb-5">Latest Entries</p>
          {stats.recent.length === 0 && <p className="text-sm text-[#6E6B63]">No registrations yet.</p>}
          <div className="flex flex-col gap-4">
            {stats.recent.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-4 text-sm">
                <div>
                  <p className="text-[#F6F4EE]/90">{r.name || r.team_name}</p>
                  <p className="text-xs text-[#6E6B63]">{r.college}</p>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#B39343]">{r.type}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function RegistrationsTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (type) params.type = type;
    if (q) params.q = q;
    api.get("/admin/registrations", { params }).then((r) => setRows(r.data)).catch(() => toast.error("Failed to load registrations"));
  }, [type, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/registrations/${id}`, { status }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this registration?")) return;
    await api.delete(`/admin/registrations/${id}`).catch(() => toast.error("Delete failed"));
    toast.success("Registration removed");
    load();
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/registrations/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "finatex26_registrations.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="admin-registrations">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <input data-testid="admin-reg-search" className="input-lux max-w-xs" placeholder="Search name, college, token..." value={q} onChange={(e) => setQ(e.target.value)} />
        <select data-testid="admin-reg-type-filter" className="input-lux max-w-[180px]" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All Types</option>
          <option value="individual">Individual</option>
          <option value="group">Group</option>
        </select>
        <button onClick={exportCsv} data-testid="admin-reg-export" className="inline-flex items-center gap-2 border border-[#D4AF37]/50 px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#08080A]">
          <Download size={14} /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto border border-white/8">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead>
            <tr className="border-b border-white/8 bg-[#111116] font-mono text-[10px] uppercase tracking-[0.2em] text-[#B39343]">
              <th className="px-4 py-3">Token</th>
              <th className="px-4 py-3">Name / Team</th>
              <th className="px-4 py-3">College</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Arenas</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} data-testid={`admin-reg-row-${i}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-4 py-3 font-mono text-xs text-[#E5C97A]">{r.token_id}</td>
                <td className="px-4 py-3 text-[#F6F4EE]/90">{r.name || r.team_name}</td>
                <td className="px-4 py-3 text-[#A19E95]">{r.college}</td>
                <td className="px-4 py-3 text-xs uppercase text-[#A19E95]">{r.type}</td>
                <td className="max-w-[220px] px-4 py-3 text-xs text-[#A19E95]">{r.event || (r.events || []).join(", ")}</td>
                <td className="px-4 py-3">
                  <select
                    data-testid={`admin-reg-status-${i}`}
                    value={r.status}
                    onChange={(e) => setStatus(r.id, e.target.value)}
                    className="bg-[#0F0F14] border border-white/10 px-2 py-1 text-xs text-[#F6F4EE]"
                  >
                    <option value="confirmed">confirmed</option>
                    <option value="waitlisted">waitlisted</option>
                    <option value="cancelled">cancelled</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-xs text-[#6E6B63]">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3">
                  <button onClick={() => remove(r.id)} data-testid={`admin-reg-delete-${i}`} className="text-[#6E6B63] hover:text-[#E74C3C] transition-colors" aria-label="Delete registration">
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-[#6E6B63]">No registrations found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EnquiriesTab() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/enquiries").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load enquiries"));
  }, []);
  useEffect(load, [load]);

  const toggleRead = async (id, read) => {
    await api.patch(`/admin/enquiries/${id}`, { read }).catch(() => toast.error("Update failed"));
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/admin/enquiries/${id}`).catch(() => toast.error("Delete failed"));
    load();
  };

  return (
    <div className="flex flex-col gap-4" data-testid="admin-enquiries">
      {rows.length === 0 && <p className="text-sm text-[#6E6B63]">No enquiries on the table yet.</p>}
      {rows.map((e, i) => (
        <div key={e.id} data-testid={`admin-enquiry-${i}`} className={`border p-6 ${e.read ? "border-white/8 bg-[#111116]" : "border-[#D4AF37]/35 bg-[#13131A]"}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <p className="font-display text-lg font-semibold text-[#F6F4EE]">{e.name}</p>
                {!e.read && <span className="bg-[#D4AF37] px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] text-[#08080A]">New</span>}
              </div>
              <p className="mt-1 text-xs text-[#A19E95]">{e.email} · {e.phone}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#B39343]">{e.subject} · {fmtDate(e.created_at)}</p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => toggleRead(e.id, !e.read)} data-testid={`admin-enquiry-read-${i}`} className="border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-[#A19E95] hover:border-[#D4AF37]/50 hover:text-[#F6F4EE] transition-colors">
                {e.read ? "Mark Unread" : "Mark Read"}
              </button>
              <button onClick={() => remove(e.id)} data-testid={`admin-enquiry-delete-${i}`} className="text-[#6E6B63] hover:text-[#E74C3C] transition-colors" aria-label="Delete enquiry">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-[#F6F4EE]/80">{e.message}</p>
        </div>
      ))}
    </div>
  );
}

const emptyEvent = {
  name: "",
  college_level: "Senior College",
  category: "",
  short_description: "",
  full_description: "",
  image_url: "",
  registration_format: "",
  team_size: "",
  rounds: "",
  prize: "",
  rules: "",
  eligibility: "",
  event_date: "",
  event_time: "",
  venue: "",
  registration_deadline: "",
  status: "Open",
  featured: false,
};

const EVENT_STATUSES = ["Upcoming", "Open", "Closed", "Completed"];
const COLLEGE_LEVELS = ["Senior College", "Junior College"];

function EventsTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/events").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load events"));
  }, []);
  useEffect(load, [load]);

  const esrc = (ev) => (ev.storage_path ? `${process.env.REACT_APP_BACKEND_URL}/api/events/image/${ev.id}` : ev.image_url);

  const openEdit = (ev) => {
    setEditing(ev ? { ...emptyEvent, ...ev } : { ...emptyEvent });
    setFile(null);
    setFileKey((k) => k + 1);
  };

  const save = async () => {
    if (!editing.name.trim()) {
      toast.error("Event name is required");
      return;
    }
    setSaving(true);
    try {
      const { id, storage_path, content_type, order, ...payload } = editing;
      let eventId = id;
      if (eventId) {
        await api.put(`/admin/events/${eventId}`, payload);
      } else {
        const { data } = await api.post("/admin/events", payload);
        eventId = data.id;
      }
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/admin/events/${eventId}/image`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      }
      toast.success("Event saved");
      setEditing(null);
      setFile(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const patch = async (ev, changes) => {
    const { id, storage_path, content_type, order, ...payload } = ev;
    await api.put(`/admin/events/${id}`, { ...payload, ...changes }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    load();
  };

  const move = async (index, dir) => {
    const ids = rows.map((r) => r.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    setRows(ids.map((id) => rows.find((r) => r.id === id)));
    await api.post("/admin/events/reorder", { ids }).catch(() => toast.error("Reorder failed"));
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/admin/events/${id}`).catch(() => toast.error("Delete failed"));
    load();
  };

  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  return (
    <div data-testid="admin-events">
      <button onClick={() => openEdit(null)} data-testid="admin-event-add" className="mb-6 inline-flex items-center gap-2 bg-[#D4AF37] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#08080A] hover:bg-[#E5C97A] transition-colors">
        <Plus size={14} /> Add Event
      </button>
      <div className="flex flex-col gap-3">
        {rows.map((ev, i) => (
          <div key={ev.id} data-testid={`admin-event-row-${i}`} className="flex flex-wrap items-center justify-between gap-4 border border-white/8 bg-[#111116] px-5 py-4">
            <div className="flex min-w-0 items-center gap-4">
              {esrc(ev) ? (
                <img src={esrc(ev)} alt="" className="h-12 w-16 shrink-0 border border-white/10 object-cover" />
              ) : (
                <span className="flex h-12 w-16 shrink-0 items-center justify-center border border-dashed border-white/10 font-mono text-[8px] tracking-[0.15em] text-[#6E6B63]">NO POSTER</span>
              )}
              <div className="min-w-0">
                <p className="truncate font-display font-semibold text-[#F6F4EE]">
                  {ev.name}
                  {ev.featured && <Star size={12} className="ml-2 inline text-[#D4AF37]" fill="#D4AF37" />}
                </p>
                <p className="text-xs text-[#6E6B63]">
                  {ev.college_level}{ev.category ? ` · ${ev.category}` : ""}
                  {ev.prize ? <span className="text-[#E5C97A]"> · {ev.prize}</span> : null}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <select
                data-testid={`admin-event-status-${i}`}
                value={ev.status}
                onChange={(e) => patch(ev, { status: e.target.value })}
                className="border border-white/10 bg-[#0F0F14] px-2 py-1.5 text-xs text-[#F6F4EE]"
              >
                {EVENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={() => patch(ev, { featured: !ev.featured })} data-testid={`admin-event-featured-${i}`} className={`border p-2 transition-colors ${ev.featured ? "border-[#D4AF37]/60 text-[#D4AF37]" : "border-white/10 text-[#6E6B63] hover:text-[#D4AF37]"}`} aria-label="Toggle featured">
                <Star size={14} fill={ev.featured ? "#D4AF37" : "none"} />
              </button>
              <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`admin-event-up-${i}`} className="border border-white/10 p-2 text-[#A19E95] transition-colors hover:text-[#D4AF37] disabled:opacity-30" aria-label="Move up">
                <ArrowUp size={14} />
              </button>
              <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} data-testid={`admin-event-down-${i}`} className="border border-white/10 p-2 text-[#A19E95] transition-colors hover:text-[#D4AF37] disabled:opacity-30" aria-label="Move down">
                <ArrowDown size={14} />
              </button>
              <button onClick={() => openEdit(ev)} data-testid={`admin-event-edit-${i}`} className="text-[#A19E95] transition-colors hover:text-[#D4AF37]" aria-label="Edit event">
                <Pencil size={15} />
              </button>
              <button onClick={() => remove(ev.id)} data-testid={`admin-event-delete-${i}`} className="text-[#6E6B63] transition-colors hover:text-[#E74C3C]" aria-label="Delete event">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto border-[#D4AF37]/25 bg-[#0F0F14] sm:max-w-2xl" data-testid="admin-event-modal">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl text-[#F6F4EE]">{editing.id ? "Edit Event" : "New Event"}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><label className="label-lux">Event Name *</label><input data-testid="admin-event-name" className="input-lux" value={editing.name} onChange={set("name")} /></div>
                <div>
                  <label className="label-lux">College Level</label>
                  <select data-testid="admin-event-level" className="input-lux" value={editing.college_level} onChange={set("college_level")}>
                    {COLLEGE_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
                <div><label className="label-lux">Category</label><input data-testid="admin-event-category" className="input-lux" value={editing.category} onChange={set("category")} /></div>
                <div className="sm:col-span-2"><label className="label-lux">Short Description</label><input data-testid="admin-event-short-desc" className="input-lux" value={editing.short_description} onChange={set("short_description")} placeholder="One line shown on the event card" /></div>
                <div className="sm:col-span-2"><label className="label-lux">Full Description</label><textarea rows={3} data-testid="admin-event-full-desc" className="input-lux resize-none" value={editing.full_description} onChange={set("full_description")} /></div>
                <div className="border border-white/8 p-4 sm:col-span-2">
                  <label className="label-lux">Event Image / Poster</label>
                  {esrc(editing) && !file && <img src={esrc(editing)} alt="" className="mb-3 h-28 w-44 border border-white/10 object-cover" />}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="inline-flex cursor-pointer items-center justify-center border border-[#D4AF37]/50 px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10">
                      {file ? file.name : "Upload Image"}
                      <input key={fileKey} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" data-testid="admin-event-image-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    </label>
                    <input className="input-lux disabled:opacity-40" placeholder="...or paste image URL" value={editing.image_url || ""} onChange={set("image_url")} disabled={!!file} data-testid="admin-event-image-url" />
                  </div>
                  {file && (
                    <p className="mt-2 font-mono text-[10px] text-[#B39343]">
                      {file.name} — uploads on save{" "}
                      <button className="text-[#6E6B63] underline" onClick={() => { setFile(null); setFileKey((k) => k + 1); }}>clear</button>
                    </p>
                  )}
                </div>
                <div><label className="label-lux">Registration Format</label><input data-testid="admin-event-format" className="input-lux" value={editing.registration_format} onChange={set("registration_format")} placeholder="e.g., Individual / Team" /></div>
                <div><label className="label-lux">Team Size</label><input data-testid="admin-event-team-size" className="input-lux" value={editing.team_size} onChange={set("team_size")} placeholder="e.g., 3-4 members" /></div>
                <div><label className="label-lux">Rounds</label><input data-testid="admin-event-rounds" className="input-lux" value={editing.rounds} onChange={set("rounds")} placeholder="e.g., 3 rounds" /></div>
                <div><label className="label-lux">Prize</label><input data-testid="admin-event-prize" className="input-lux" value={editing.prize} onChange={set("prize")} /></div>
                <div><label className="label-lux">Event Date</label><input data-testid="admin-event-date" className="input-lux" value={editing.event_date} onChange={set("event_date")} placeholder="e.g., 20 Nov 2026" /></div>
                <div><label className="label-lux">Event Time</label><input data-testid="admin-event-time" className="input-lux" value={editing.event_time} onChange={set("event_time")} placeholder="e.g., 11:00 AM" /></div>
                <div><label className="label-lux">Venue</label><input data-testid="admin-event-venue" className="input-lux" value={editing.venue} onChange={set("venue")} /></div>
                <div><label className="label-lux">Registration Deadline</label><input data-testid="admin-event-deadline" className="input-lux" value={editing.registration_deadline} onChange={set("registration_deadline")} placeholder="e.g., 15 Nov 2026" /></div>
                <div className="sm:col-span-2"><label className="label-lux">Rules</label><textarea rows={3} data-testid="admin-event-rules" className="input-lux resize-none" value={editing.rules} onChange={set("rules")} /></div>
                <div className="sm:col-span-2"><label className="label-lux">Eligibility</label><textarea rows={2} data-testid="admin-event-eligibility" className="input-lux resize-none" value={editing.eligibility} onChange={set("eligibility")} /></div>
                <div>
                  <label className="label-lux">Status</label>
                  <select data-testid="admin-event-status-select" className="input-lux" value={editing.status} onChange={set("status")}>
                    {EVENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <label className="flex cursor-pointer items-center gap-3 self-end pb-3">
                  <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="admin-event-featured-check" className="h-4 w-4 accent-[#D4AF37]" />
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[#A19E95]">Featured Event</span>
                </label>
              </div>
              <button onClick={save} disabled={saving} data-testid="admin-event-save" className="mt-4 w-full bg-[#D4AF37] px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#08080A] transition-colors hover:bg-[#E5C97A] disabled:opacity-50">
                {saving ? "Saving..." : "Save Event"}
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScheduleTab() {
  const [days, setDays] = useState([]);
  const [slot, setSlot] = useState({ time: "", title: "", location: "" });

  const load = useCallback(() => {
    api.get("/schedule").then((r) => setDays(r.data)).catch(() => toast.error("Failed to load schedule"));
  }, []);
  useEffect(load, [load]);

  const saveTheme = async (day) => {
    await api.put(`/admin/schedule/${day.day_index}`, { theme: day.theme }).catch(() => toast.error("Update failed"));
    toast.success("Theme updated");
  };

  const addSlot = async (dayIndex) => {
    if (!slot.time.trim() || !slot.title.trim() || !slot.location.trim()) {
      toast.error("Fill time, title and location");
      return;
    }
    await api.post(`/admin/schedule/${dayIndex}/slots`, slot).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    setSlot({ time: "", title: "", location: "" });
    load();
  };

  const removeSlot = async (dayIndex, idx) => {
    await api.delete(`/admin/schedule/${dayIndex}/slots/${idx}`).catch(() => toast.error("Delete failed"));
    load();
  };

  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="admin-schedule">
      {days.map((day) => (
        <div key={day.id} className="border border-white/8 bg-[#111116] p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B39343]">{day.day}</p>
          <div className="mt-3 flex gap-2">
            <input
              data-testid={`admin-schedule-theme-${day.day_index}`}
              className="input-lux"
              value={day.theme}
              onChange={(e) => setDays(days.map((d) => (d.id === day.id ? { ...d, theme: e.target.value } : d)))}
            />
            <button onClick={() => saveTheme(day)} data-testid={`admin-schedule-theme-save-${day.day_index}`} className="shrink-0 border border-[#D4AF37]/50 px-4 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#08080A] transition-colors">
              Save
            </button>
          </div>
          <div className="mt-6 flex flex-col gap-3">
            {day.slots.map((s, idx) => (
              <div key={idx} className="flex items-center justify-between gap-4 border-b border-white/5 pb-3 text-sm">
                <div>
                  <span className="font-mono text-xs text-[#E5C97A]">{s.time}</span>
                  <p className="text-[#F6F4EE]/85">{s.title}</p>
                  <p className="text-xs text-[#6E6B63]">{s.location}</p>
                </div>
                <button onClick={() => removeSlot(day.day_index, idx)} data-testid={`admin-schedule-slot-delete-${day.day_index}-${idx}`} className="text-[#6E6B63] hover:text-[#E74C3C] transition-colors" aria-label="Delete slot">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-5 grid grid-cols-[90px_1fr] gap-2">
            <input className="input-lux" placeholder="09:00 AM" value={slot.time} onChange={(e) => setSlot({ ...slot, time: e.target.value })} data-testid={`admin-slot-time-${day.day_index}`} />
            <input className="input-lux" placeholder="Session title" value={slot.title} onChange={(e) => setSlot({ ...slot, title: e.target.value })} data-testid={`admin-slot-title-${day.day_index}`} />
            <input className="input-lux col-span-2" placeholder="Location" value={slot.location} onChange={(e) => setSlot({ ...slot, location: e.target.value })} data-testid={`admin-slot-location-${day.day_index}`} />
            <button onClick={() => addSlot(day.day_index)} data-testid={`admin-slot-add-${day.day_index}`} className="col-span-2 inline-flex items-center justify-center gap-2 border border-[#D4AF37]/50 px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#08080A] transition-colors">
              <Plus size={13} /> Add Session
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function GalleryTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ title: "", category: "", image_url: "" });
  const [file, setFile] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/gallery").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load gallery"));
  }, []);
  useEffect(load, [load]);

  const gsrc = (g) => (g.storage_path ? `${process.env.REACT_APP_BACKEND_URL}/api/gallery/image/${g.id}` : g.image_url);

  const add = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!file && !form.image_url.trim()) {
      toast.error("Choose an image file or paste an image URL");
      return;
    }
    setSaving(true);
    try {
      if (file) {
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("category", form.category);
        fd.append("file", file);
        await api.post("/admin/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      } else {
        await api.post("/admin/gallery", form);
      }
      setForm({ title: "", category: "", image_url: "" });
      setFile(null);
      setInputKey((k) => k + 1);
      toast.success("Gallery item added");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this gallery item?")) return;
    await api.delete(`/admin/gallery/${id}`).catch(() => toast.error("Delete failed"));
    load();
  };

  return (
    <div data-testid="admin-gallery">
      <div className="mb-8 border border-white/8 bg-[#111116] p-6">
        <div className="grid gap-3 sm:grid-cols-[1fr_200px]">
          <input className="input-lux" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-gallery-title" />
          <input className="input-lux" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-gallery-category" />
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
          <input
            className="input-lux disabled:opacity-40"
            placeholder="Image URL — or choose a file →"
            value={form.image_url}
            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            disabled={!!file}
            data-testid="admin-gallery-url"
          />
          <label
            data-testid="admin-gallery-file-label"
            className="inline-flex cursor-pointer items-center justify-center gap-2 border border-[#D4AF37]/50 px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10"
          >
            {file ? file.name : "Upload Photo"}
            <input
              key={inputKey}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              data-testid="admin-gallery-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </label>
          <button onClick={add} disabled={saving} data-testid="admin-gallery-add" className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#08080A] transition-colors hover:bg-[#E5C97A] disabled:opacity-50">
            <Plus size={14} /> {saving ? "Adding..." : "Add"}
          </button>
        </div>
        {file && (
          <p className="mt-3 flex items-center gap-3 font-mono text-[10px] text-[#B39343]">
            {file.name} selected
            <button onClick={() => { setFile(null); setInputKey((k) => k + 1); }} data-testid="admin-gallery-file-clear" className="text-[#6E6B63] underline hover:text-[#E74C3C]">
              clear
            </button>
          </p>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((g, i) => (
          <div key={g.id} data-testid={`admin-gallery-item-${i}`} className="group relative overflow-hidden border border-white/8">
            <img src={gsrc(g)} alt={g.title} className="aspect-[4/3] w-full object-cover" />
            <div className="flex items-center justify-between bg-[#111116] px-4 py-3">
              <div>
                <p className="text-sm text-[#F6F4EE]/90">{g.title}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#B39343]">
                  {g.category}{g.storage_path ? " · Uploaded" : ""}
                </p>
              </div>
              <button onClick={() => remove(g.id)} data-testid={`admin-gallery-delete-${i}`} className="text-[#6E6B63] hover:text-[#E74C3C] transition-colors" aria-label="Delete gallery item">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const TIERS = ["Title Partner", "Gold Partner", "Silver Partner", "Media Partner"];

function SponsorsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", tier: TIERS[1] });

  const load = useCallback(() => {
    api.get("/sponsors").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load sponsors"));
  }, []);
  useEffect(load, [load]);

  const add = async () => {
    if (!form.name.trim()) {
      toast.error("Sponsor name is required");
      return;
    }
    await api.post("/admin/sponsors", form).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    setForm({ name: "", tier: TIERS[1] });
    toast.success("Sponsor added");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this sponsor?")) return;
    await api.delete(`/admin/sponsors/${id}`).catch(() => toast.error("Delete failed"));
    load();
  };

  return (
    <div data-testid="admin-sponsors">
      <div className="mb-8 grid gap-3 border border-white/8 bg-[#111116] p-6 sm:grid-cols-[1fr_220px_auto]">
        <input className="input-lux" placeholder="Sponsor name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-sponsor-name" />
        <select className="input-lux" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="admin-sponsor-tier">
          {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button onClick={add} data-testid="admin-sponsor-add" className="inline-flex items-center gap-2 bg-[#D4AF37] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#08080A] hover:bg-[#E5C97A] transition-colors">
          <Plus size={14} /> Add
        </button>
      </div>
      <div className="flex flex-col gap-3">
        {rows.map((s, i) => (
          <div key={s.id} data-testid={`admin-sponsor-row-${i}`} className="flex items-center justify-between border border-white/8 bg-[#111116] px-6 py-4">
            <div>
              <p className="font-display font-semibold text-[#F6F4EE]">{s.name}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#B39343]">{s.tier}</p>
            </div>
            <button onClick={() => remove(s.id)} data-testid={`admin-sponsor-delete-${i}`} className="text-[#6E6B63] hover:text-[#E74C3C] transition-colors" aria-label="Delete sponsor">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-[#6E6B63]">No sponsors listed yet.</p>}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("ftx_admin_token")) {
      navigate("/admin/login");
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("ftx_admin_token");
        navigate("/admin/login");
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("ftx_admin_token");
    localStorage.removeItem("ftx_admin_user");
    api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#08080A]">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#B39343]">Shuffling the deck...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="flex min-h-screen bg-[#08080A]">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-[#D4AF37]/15 bg-[#0B0B0F] lg:flex">
        <div className="border-b border-[#D4AF37]/15 px-6 py-5">
          <Link to="/" className="flex items-center gap-2.5">
            <SpadeMark className="h-6 w-6" />
            <span className="font-bebas text-xl tracking-[0.22em] text-[#F6F4EE]">
              FINATEX<span className="text-[#D4AF37]">'26</span>
            </span>
          </Link>
          <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-[#6E6B63]">Command Center</p>
        </div>
        <nav className="flex flex-1 flex-col gap-1 px-3 py-5">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-3 px-4 py-3 text-left text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
                tab === t.key ? "bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]" : "text-[#A19E95] hover:text-[#F6F4EE] border-l-2 border-transparent"
              }`}
            >
              <t.icon size={15} />
              {t.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-[#D4AF37]/15 px-6 py-5">
          <p className="truncate text-xs text-[#6E6B63]">{user.email}</p>
          <button onClick={logout} data-testid="admin-logout-btn" className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#A19E95] hover:text-[#E74C3C] transition-colors">
            <LogOut size={13} /> Leave the Table
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-[#D4AF37]/15 bg-[#0B0B0F] px-5 py-4 lg:hidden">
          <span className="font-bebas text-xl tracking-[0.22em] text-[#F6F4EE]">
            FINATEX<span className="text-[#D4AF37]">'26</span>
          </span>
          <button onClick={logout} data-testid="admin-logout-btn-mobile" className="text-[#A19E95]" aria-label="Logout">
            <LogOut size={17} />
          </button>
        </div>
        <div className="flex gap-2 overflow-x-auto border-b border-white/8 bg-[#0B0B0F] px-4 py-3 lg:hidden">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-mobile-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`shrink-0 px-4 py-2 text-[10px] uppercase tracking-[0.18em] transition-colors ${
                tab === t.key ? "bg-[#D4AF37] text-[#08080A]" : "text-[#A19E95] border border-white/10"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <main className="flex-1 px-5 py-8 sm:px-8 lg:px-10">
          <div className="mb-8">
            <p className="eyebrow">{TABS.find((t) => t.key === tab)?.label}</p>
            <h1 className="mt-2 font-display text-2xl sm:text-3xl font-bold text-[#F6F4EE]">
              {tab === "overview" && "The Count Room"}
              {tab === "registrations" && "The Guest Ledger"}
              {tab === "enquiries" && "Letters to the House"}
              {tab === "events" && "Manage the Events"}
              {tab === "schedule" && "The Programme Board"}
              {tab === "gallery" && "The Picture Gallery"}
              {tab === "sponsors" && "The Backers"}
              {tab === "hero" && "The Silver Screen"}
            </h1>
          </div>
          {tab === "overview" && <OverviewTab />}
          {tab === "registrations" && <RegistrationsTab />}
          {tab === "enquiries" && <EnquiriesTab />}
          {tab === "events" && <EventsTab />}
          {tab === "schedule" && <ScheduleTab />}
          {tab === "gallery" && <GalleryTab />}
          {tab === "sponsors" && <SponsorsTab />}
          {tab === "hero" && <HeroMediaTab />}
        </main>
      </div>
    </div>
  );
}
